import {EventSearchDto, ShiftEventsSortBy, SortOrder, Shift} from '@hconnect/common/types'
import {UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'

import {FilterOptions} from '../../types'
import {mapShiftEvent} from '../mappers'
import {EventSearchResult} from '../types'
import {buildEventSearchCriteria} from '../utils'

import {useEventsSearchApi} from './useEventsSearchApi'

type UseEventsSearchParams = {
  plantId: string
  timezone: string
  shifts: Shift[]
  getPlantNow: () => Moment
  input: FilterOptions
  sortBy?: ShiftEventsSortBy
  sortOrder?: SortOrder
  pageNumber?: number
  itemsPerPage?: number
}

type UseEventsSearchOptions = Omit<
  UseQueryOptions<EventSearchDto, AxiosError, EventSearchResult>,
  'queryKey' | 'queryFn' | 'select'
>

/**
 * High-level hook that accepts UI-level FilterOptions, converts them to API criteria,
 * and maps the response DTOs to domain ShiftEvent objects (with Moment dates).
 *
 * For direct API access without filter conversion or mapping, use `useEventsSearchApi`.
 */
export const useEventsSearch = (
  {
    plantId,
    timezone,
    shifts,
    getPlantNow,
    input,
    sortBy = ShiftEventsSortBy.Details,
    sortOrder,
    pageNumber = 0,
    itemsPerPage = 50
  }: UseEventsSearchParams,
  options?: UseEventsSearchOptions
) => {
  const criteria = buildEventSearchCriteria({
    shifts,
    getPlantNow,
    input,
    sortBy,
    sortOrder,
    pageNumber,
    itemsPerPage
  })

  const select = useCallback(
    (dto: EventSearchDto): EventSearchResult => ({
      ...dto,
      events: (dto.events || []).map((event) => mapShiftEvent(event, timezone))
    }),
    [timezone]
  )

  return useEventsSearchApi<EventSearchResult>(
    {plantId, criteria},
    {
      ...options,
      select
    }
  )
}
